/* ============================================================================
 * hfb-motion.js — the ONE reveal chokepoint for a bespoke Horizon theme.
 *
 * Grafted at provision, labelled `origin: hfb`, never refreshed by a Horizon
 * upgrade. Its twin is assets/hfb-motion.css; the consumer is
 * snippets/hfb-reveal.liquid.
 *
 * THERE IS EXACTLY ONE OBSERVER AND ONE STATE CLASS (`is-revealed`), site-wide.
 * A second reveal mechanism authored inside a section is the failure this file
 * exists to prevent: per-section motion drifts within a single build and cannot
 * be retuned without touching every file. craft-floor-check.mjs (M3) fails a
 * theme that grows one. (It deliberately does NOT object to other state classes
 * — a carousel's `.is-active` is legitimate and unrelated.)
 *
 * READINESS IS THE SAFETY CONTRACT, AND THE ORDER MATTERS.
 * The CSS hides nothing until <html> carries `hfb-motion-ready`. This file adds
 * that class ONLY after the observer exists AND every reveal element has been
 * registered, and removes it again on any failure. So a partial init cannot
 * leave a subset of elements hidden with nothing watching them — which is the
 * precise way a "safe" reveal system ships a blank page.
 * ========================================================================== */
(function () {
  'use strict';

  var READY = 'hfb-motion-ready';
  var root = document.documentElement;
  // SHOWCASE TIER ONLY (<html data-motion-tier="showcase">, set by execute-theme on a
  // Showcase build). Every branch below that reads SHOWCASE is inert on any other tier, so
  // a Signature/Baseline/Static theme behaves exactly as it did before this was added.
  var SHOWCASE = root.getAttribute('data-motion-tier') === 'showcase';
  var SELECTOR = '.hfb-reveal, .hfb-reveal-stagger, .hfb-clip-reveal' +
    (SHOWCASE ? ', .hfb-sc-img, [data-hfb-sc-reveal="line-mask"]' : '');
  var SC_IMAGE_WAIT_MS = 4000; // a photo slower than this fades in plainly (never pops)
  var SC_GROUP_CAP_S = 1.2;    // a stagger group never takes longer than this to start its last item

  function revealAll(nodes) {
    for (var i = 0; i < nodes.length; i++) nodes[i].classList.add('is-revealed');
  }

  /* --- Showcase image reveals: play only once the photo has loaded ----------
   * A photo that arrives late still gets its full reveal when it lands. One that
   * fails, or is slower than SC_IMAGE_WAIT_MS, gets data-hfb-sc-state="plain"
   * (invisible, no wipe) and then "shown" (a plain fade) — it never pops in, and
   * a timer guarantees it is never left invisible. */
  function scWhenReady(img, done) {
    var settled = false;
    function finish(ok) { if (!settled) { settled = true; done(ok); } }
    function decode() {
      if (img.decode) img.decode().then(function () { finish(true); }, function () { finish(img.naturalWidth > 0); });
      else finish(img.naturalWidth > 0);
    }
    if (!img) return finish(false);
    // `complete` with no pixels = it already FAILED: settle now, not after the timeout
    if (img.complete) { if (img.naturalWidth > 0) decode(); else finish(false); }
    else {
      img.addEventListener('load', decode);
      img.addEventListener('error', function () { finish(false); });
    }
    setTimeout(function () { finish(false); }, SC_IMAGE_WAIT_MS);
  }
  function scShowPlain(frame, img) {
    frame.setAttribute('data-hfb-sc-state', 'plain');
    var shown = false;
    function show() {
      if (shown) return;
      shown = true;
      requestAnimationFrame(function () { requestAnimationFrame(function () { frame.setAttribute('data-hfb-sc-state', 'shown'); }); });
    }
    // `complete` is also true for a FAILED image — show it, never wait for an event that fired
    if (!img || img.complete) return show();
    img.addEventListener('load', show);
    img.addEventListener('error', show);
    setTimeout(show, SC_IMAGE_WAIT_MS);
  }
  function scSettleImage(frame) {
    var img = frame.querySelector('img');
    // Chrome never starts a lazy <img> while it is fully clipped (the hidden reveal state),
    // so a lazy photo would not load until something unclipped it. Fetch it now.
    if (img && img.loading === 'lazy') img.loading = 'eager';
    scWhenReady(img, function (ok) {
      if (ok) frame.classList.add('is-revealed');
      else scShowPlain(frame, img);
    });
  }
  /* --- Showcase group stagger: 0 for one item, else min(stagger, 1.2s / (n - 1)) */
  function scSpaceGroups() {
    var raw = getComputedStyle(root).getPropertyValue('--hfb-sc-stagger') || '';
    var m = /([\d.]+)\s*(ms|s)/.exec(raw);
    var stagger = m ? (m[2] === 'ms' ? parseFloat(m[1]) / 1000 : parseFloat(m[1])) : 0.22;
    var hosts = document.querySelectorAll('.hfb-reveal-stagger');
    for (var h = 0; h < hosts.length; h++) {
      var kids = hosts[h].children;
      var n = kids.length;
      var spacing = n > 1 ? Math.min(stagger, SC_GROUP_CAP_S / (n - 1)) : 0;
      for (var k = 0; k < n; k++) kids[k].style.setProperty('--hfb-reveal-delay', Math.round(k * spacing * 1000) + 'ms');
    }
  }

  function init() {
    var nodes = document.querySelectorAll(SELECTOR);

    // No IntersectionObserver (or no elements): never arm the hiding CSS. The
    // page renders final-state. This is a supported outcome, not a fallback
    // failure — the class is simply never added.
    if (!('IntersectionObserver' in window)) {
      root.classList.remove(READY);
      revealAll(nodes);
      return;
    }
    if (!nodes.length) return;

    var observer;
    try {
      observer = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          var target = entries[i].target;
          observer.unobserve(target);
          // Showcase image frames reveal only once their photo has loaded.
          if (SHOWCASE && target.classList.contains('hfb-sc-img')) { scSettleImage(target); continue; }
          target.classList.add('is-revealed');
        }
      }, SHOWCASE
        // Showcase: start the moment an element enters the screen.
        ? { rootMargin: '0px', threshold: 0 }
        : { rootMargin: '0px 0px -10% 0px', threshold: 0.01 });


      if (SHOWCASE) {
        scSpaceGroups();
        // Frames already on screen at start show as-is — no hide-then-reveal flash.
        var vh = window.innerHeight || 0;
        for (var j = 0; j < nodes.length; j++) {
          if (!nodes[j].classList.contains('hfb-sc-img')) continue;
          var r = nodes[j].getBoundingClientRect();
          if (r.bottom > 0 && r.top < vh) nodes[j].setAttribute('data-hfb-sc-state', 'done');
        }
      }

      // Register EVERY element before arming the CSS. If any observe() throws,
      // the catch below un-arms and reveals — we never half-hide a page.
      // (Showcase: a frame already on screen is final — `done` — and is never observed, or
      // the load-wait could fade a slow, already-visible photo OUT.)
      for (var i = 0; i < nodes.length; i++) {
        if (SHOWCASE && nodes[i].getAttribute('data-hfb-sc-state') === 'done') continue;
        observer.observe(nodes[i]);
      }
    } catch (e) {
      root.classList.remove(READY);
      revealAll(nodes);
      if (observer && observer.disconnect) observer.disconnect();
      return;
    }

    // Registration complete — only now is it safe for the CSS to hide anything.
    root.classList.add(READY);

    // Anything already in view at load reveals on the observer's first callback,
    // which fires immediately; no separate above-the-fold sweep is needed.
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
